import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import { supabase } from '@/lib/supabase';

export interface LessonRow {
  id: string;
  title: string;
  group_id: string | null;
  level: string | null;
  subject: string | null;
  date: string | null;
  start_time: string | null;
  end_time: string | null;
  type: string | null;
  live_link: string | null;
  description: string | null;
  status: string;
  has_record: boolean;
  has_document: boolean;
  has_assignment: boolean;
}

export interface GroupInfo {
  name: string;
  color: string;
}

interface LessonEditModalProps {
  open: boolean;
  onClose: () => void;
  lesson: LessonRow | null;
  groupMap: Record<string, GroupInfo>;
  onSaved: () => void;
  onDeleted: () => void;
}

export default function LessonEditModal({
  open,
  onClose,
  lesson,
  groupMap,
  onSaved,
  onDeleted,
}: LessonEditModalProps) {
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [type, setType] = useState('grup');
  const [liveLink, setLiveLink] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('planlandi');

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && lesson) {
      setTitle(lesson.title ?? '');
      setSubject(lesson.subject ?? '');
      setDate(lesson.date ?? '');
      setStartTime(lesson.start_time ?? '');
      setEndTime(lesson.end_time ?? '');
      setType(lesson.type ?? 'grup');
      setLiveLink(lesson.live_link ?? '');
      setDescription(lesson.description ?? '');
      setStatus(lesson.status ?? 'planlandi');
      setConfirmDelete(false);
      setError(null);
    }
  }, [open, lesson]);

  if (!lesson) return null;

  const group = lesson.group_id ? groupMap[lesson.group_id] : undefined;

  async function handleSave(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!title.trim()) {
      setError('Lütfen ders başlığını girin.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { error: updateError } = await supabase
        .from('zehrahoca_lessons')
        .update({
          title: title.trim(),
          subject: subject.trim() || null,
          date: date || null,
          start_time: startTime || null,
          end_time: endTime || null,
          type,
          live_link: liveLink.trim() || null,
          description: description.trim() || null,
          status,
        })
        .eq('id', lesson.id);
      if (updateError) throw updateError;
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Ders güncellenirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    setError(null);
    try {
      const lessonId = lesson.id;
      const { data: assignmentRows } = await supabase
        .from('zehrahoca_assignments')
        .select('id')
        .eq('lesson_id', lessonId);
      const assignmentIds = (assignmentRows ?? []).map((a: { id: string }) => a.id);
      if (assignmentIds.length > 0) {
        await supabase.from('zehrahoca_assignment_submissions').delete().in('assignment_id', assignmentIds);
      }
      await supabase.from('zehrahoca_assignments').delete().eq('lesson_id', lessonId);
      await supabase.from('zehrahoca_documents').delete().eq('lesson_id', lessonId);
      await supabase.from('zehrahoca_lesson_records').delete().eq('lesson_id', lessonId);
      const { error: delError } = await supabase.from('zehrahoca_lessons').delete().eq('id', lessonId);
      if (delError) throw delError;
      onDeleted();
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Ders silinirken bir hata oluştu.');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Dersi Düzenle"
      subtitle={group ? `${group.name}${lesson.level ? ` · ${lesson.level}` : ''}` : 'Ders bilgilerini güncelleyin'}
    >
      <form onSubmit={handleSave} className="space-y-5">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ders Başlığı</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ders Konusu</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Tarih</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ders Türü</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="grup">Grup Dersi</option>
              <option value="birebir">Birebir Ders</option>
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Başlangıç Saati</label>
            <input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Bitiş Saati</label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Canlı Ders Linki</label>
            <input
              type="url"
              value={liveLink}
              onChange={(e) => setLiveLink(e.target.value)}
              placeholder="https://meet.google.com/..."
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ders Durumu</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="planlandi">Planlandı</option>
              <option value="islendi">İşlendi</option>
              <option value="iptal">İptal</option>
              <option value="ertelendi">Ertelendi</option>
              <option value="kayit_yuklendi">Kayıt Yüklendi</option>
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ders Açıklaması</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full resize-none rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700">
            <i className="ri-error-warning-line mt-0.5 text-base" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 border-t border-background-200 pt-5">
          {confirmDelete ? (
            <div className="flex items-center gap-2 text-sm">
              <span className="font-semibold text-red-700">Bu dersi silmek istediğinize emin misiniz?</span>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="whitespace-nowrap rounded-lg bg-red-500 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-red-600 disabled:opacity-70 cursor-pointer"
              >
                {deleting ? 'Siliniyor...' : 'Evet, Sil'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold text-foreground-600 transition hover:bg-background-100 cursor-pointer"
              >
                Vazgeç
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-red-200 bg-white px-3.5 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50 cursor-pointer"
            >
              <i className="ri-delete-bin-6-line" /> Dersi Sil
            </button>
          )}

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="whitespace-nowrap rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:opacity-70 cursor-pointer"
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}